import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import { SETUP_COMPLETE_LOCAL_KEY } from "../../config";
import brandingBg from '../../assets/images/bot-avatar.jpg';

import {
  Box, Typography, TextField, Button, Alert, useTheme, CircularProgress,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

// KlonIT Logo SVG
const KlonITLogo = ({ color = "white", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
  </svg>
);

const colors = {
  sidebarBg: '#1e293b',
  sidebarHover: '#313d4a',
  sidebarText: '#dee4ee',
  sidebarTextMuted: '#8a99af',
  accent: '#3b82f6',
  bgPrimary: '#f8fafc',
  bgMuted: '#f1f5f9',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  success: '#059669',
  danger: '#dc2626',
};

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const FeatureCard = ({ icon, title, description }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2,
    p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
    transition: 'all 0.25s ease',
    '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', transform: 'translateX(4px)' },
  }}>
    <Box sx={{
      width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colors.accent,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5 }}>{description}</Typography>
    </Box>
  </Box>
);

export default function VerifiedEmail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token } = useParams();

  // --- States for initial verification ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");

  // --- States for resend action ---
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendSuccessMessage, setResendSuccessMessage] = useState(null);
  const [emailForResend, setEmailForResend] = useState("");

  useEffect(() => {
    document.title = "Email Verification | KlonIT Web Application";

    const verifyEmail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`/verify/${token}`);
        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
        const { customer_portal_token, user, message: apiMessage } = responseData;
        localStorage.setItem("user", JSON.stringify(user));
        if (customer_portal_token) {
          localStorage.setItem('customer_portal_token', customer_portal_token);
        }

        setSuccess(true);
        setMessage(apiMessage || "Email successfully verified!");
      } catch (err) {
        console.error("API Error:", err);
        let errorMessage;
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response) {
          errorMessage = `Server returned ${err.response.status}. Please try again or request a new link.`;
        } else if (err.request) {
          errorMessage = "Could not reach the server. Please check your connection and try again.";
        } else {
          errorMessage = err.message || "Something went wrong while verifying your email.";
        }
        setError(errorMessage);
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyEmail();
    } else {
      setError("Verification token is missing from the URL.");
      setLoading(false);
    }
  }, [token]);

  const handleProceedNext = () => {
    window.location.href = "/trading-portal/dashboard";
  };

  const handleResendEmail = async (e) => {
    e.preventDefault();
    if (!emailForResend) {
      setResendError("Please enter your email address.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(emailForResend)) {
      setResendError("Please enter a valid email address.");
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendSuccessMessage(null);

    try {
      const response = await axiosInstance.post(`/resend-verification`, {
        email: emailForResend,
      });
      setResendSuccessMessage(response.data.message);
    } catch (err) {
      console.error("Resend API Error:", err);
      const errorMessage = err.response?.data?.message || "Failed to resend email. Please try again.";
      setResendError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
      border: 'none', outline: 'none',
      '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
      '& fieldset': { display: 'none !important' },
      '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
    },
    '& .MuiInputBase-input': {
      fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
      border: 'none', outline: 'none',
      '&:focus': { outline: 'none', boxShadow: 'none' },
      '&::placeholder': { color: colors.textTertiary, opacity: 1 },
    },
    '& .MuiInputAdornment-root': { color: colors.textTertiary },
    '& .MuiInputLabel-root': { fontFamily },
  };

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');`}</style>

      <Box sx={{ fontFamily, minHeight: '100vh', bgcolor: colors.bgPrimary, display: 'flex' }}>
        {/* Left Branding Panel */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' }, width: '50%',
          flexDirection: 'column', justifyContent: 'center', p: '4rem', position: 'relative', overflow: 'hidden',
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.9)), url(${brandingBg})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          '&::before': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.08) 0%, transparent 40%)',
          },
        }}>
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
              <Box sx={{
                width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
              }}>
                <KlonITLogo />
              </Box>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 2, py: 0.5, bgcolor: colors.sidebarHover,
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
              fontSize: '0.8125rem', fontWeight: 600, color: colors.accent, mb: 2, letterSpacing: '0.05em',
            }}>
              <BoltIcon sx={{ fontSize: 14 }} />
              CLONE · SCALE · GROW
            </Box>

            <Typography sx={{ fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1.5, fontFamily }}>
              AI-Powered<br /><Box component="span" sx={{ color: colors.accent }}>Business Growth</Box>
            </Typography>

            <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted, mb: 2.5, fontFamily }}>
              Deploy intelligent AI agents that engage customers 24/7, automate workflows, and scale your business across 52+ languages.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <FeatureCard icon={<ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />} title="24/7 AI Agents" description="Engage customers on web, WhatsApp & email" />
              <FeatureCard icon={<LanguageIcon sx={{ fontSize: 20 }} />} title="52+ Languages" description="Scale globally with native language support" />
            </Box>
          </Box>
        </Box>

        {/* Right Form Panel */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: { xs: 2, lg: '3rem 4rem' }, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)`,
          '&::before': {
            content: '""', position: 'absolute', top: '-30%', right: '-20%',
            width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
            background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
          },
          '&::after': {
            content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
            width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
          },
        }}>
          <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
            {/* Mobile Logo */}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', justifyContent: 'center', gap: '0.75rem', mb: 2 }}>
              <Box sx={{ width: 44, height: 44, bgcolor: colors.sidebarBg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KlonITLogo color="white" />
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            {/* Loading State */}
            {loading && (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '16px', mx: 'auto', mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                }}>
                  <CircularProgress size={28} sx={{ color: '#fff' }} />
                </Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5, fontFamily }}>
                  Verifying Email
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: colors.textTertiary, fontFamily }}>
                  Please wait while we verify your email address...
                </Typography>
              </Box>
            )}

            {/* Success State */}
            {!loading && success && (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '16px', mx: 'auto', mb: 2,
                  background: `linear-gradient(135deg, ${colors.success}, #047857)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 8px 24px ${colors.success}40`,
                }}>
                  <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                </Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5, fontFamily }}>
                  Email Verified!
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: colors.textTertiary, fontFamily, mb: 3 }}>
                  {message || "Your email has been successfully verified."}
                </Typography>

                <Alert severity="success" sx={{ mb: 2, borderRadius: '12px', fontFamily, fontSize: '0.8125rem' }}>
                  {message || "Your email has been successfully verified."}
                </Alert>

                <Button
                  onClick={handleProceedNext}
                  fullWidth
                  sx={{
                    py: '0.875rem', fontFamily, fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: '#fff', borderRadius: '14px', height: 48,
                    boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Go to Trading Dashboard
                </Button>
              </Box>
            )}

            {/* Error State */}
            {!loading && error && !success && (
              <Box>
                {/* Header with icon */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '16px', mx: 'auto', mb: 2,
                    background: `linear-gradient(135deg, ${colors.danger}, #b91c1c)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 8px 24px ${colors.danger}30`,
                  }}>
                    <ErrorOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                  </Box>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5, fontFamily }}>
                    Verification Failed
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: colors.textTertiary, fontFamily }}>
                    We couldn't verify your email address
                  </Typography>
                </Box>

                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily, fontSize: '0.8125rem' }}>
                  {error}
                </Alert>

                {/* Resend Form */}
                <Box sx={{
                  p: 2.5, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.textPrimary, mb: 0.5, fontFamily }}>
                    Resend Verification Email
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, mb: 2, fontFamily }}>
                    Enter your email to receive a new verification link
                  </Typography>

                  {resendError && (
                    <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem' }}>
                      {resendError}
                    </Alert>
                  )}
                  {resendSuccessMessage && (
                    <Alert severity="success" sx={{ mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem' }}>
                      {resendSuccessMessage}
                    </Alert>
                  )}

                  <form onSubmit={handleResendEmail}>
                    <TextField
                      fullWidth size="small" type="email" placeholder="Enter your email address"
                      value={emailForResend} onChange={(e) => setEmailForResend(e.target.value)}
                      required disabled={resendLoading}
                      InputProps={{ startAdornment: <EmailOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
                      sx={{ ...inputSx, mb: 1.5 }}
                    />

                    <Button
                      type="submit" fullWidth disabled={resendLoading}
                      startIcon={resendLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                      sx={{
                        py: '0.75rem', fontFamily, fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: '#fff', borderRadius: '14px', height: 46,
                        boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': { opacity: 0.7 },
                      }}
                    >
                      {resendLoading ? "Sending..." : "Resend Verification Email"}
                    </Button>
                  </form>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                    Remember your account?{' '}
                    <Box component="a" href="/SignIn" sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Sign In
                    </Box>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
